import './styles.css';
import './ui-theme.css';
import { ACTIONS, DEFAULT_BINDINGS, KEY_OPTIONS, keyLabel } from './keybindings';
let bindings = structuredClone(DEFAULT_BINDINGS);
import { Game } from './game/Game';
import { CHIP_DEFS, CREATURE_DEFS, DYNAMITE_PENALTY, MINERAL_TIME_CAP, levelCurve, cumulativeTarget, LEVEL_NAMES, MINERAL_DEFS, type MineralKind, type CreatureKind, type ChipId, type GameMode } from './game/config';
import {drawStageOffers} from './game/progression';
import { STAGES, stageForLevel } from './game/stages';
import { mineralIcon, toolIcon, creatureIcon } from './ui-art';
import type { ChipSlotName } from './game/chips';
import {type MatchOptions} from './game/match';
import { AudioSystem } from './systems/AudioSystem';

const canvas = document.querySelector<HTMLCanvasElement>('#game-canvas');
if (!canvas) throw new Error('Missing #game-canvas');

const stageViewport = document.querySelector<HTMLElement>('#game-stage')!;
const mobileQuery = matchMedia('(pointer: coarse)');
function fitCanvas(): void {
  const mobile = mobileQuery.matches;
  const rotated = mobile && innerHeight > innerWidth;
  const viewWidth = rotated ? innerHeight : innerWidth;
  const viewHeight = rotated ? innerWidth : innerHeight;
  const app = document.querySelector<HTMLElement>('#app')!;
  app.dataset.mobile = String(mobile);
  app.dataset.rotated = String(rotated);
  app.style.setProperty('--view-width', `${viewWidth}px`);
  app.style.setProperty('--view-height', `${viewHeight}px`);
  const width = Math.max(1, Math.min(viewWidth - (mobile ? 144 : 0), viewHeight * 3 / 2));
  const height = width * 2 / 3;
  app.style.setProperty('--scene-width', `${width}px`);
  app.style.setProperty('--scene-height', `${height}px`);
  stageViewport.style.width = `${width}px`;
  stageViewport.style.height = `${height}px`;
  canvas!.style.width = `${width}px`;
  canvas!.style.height = `${height}px`;
}
mobileQuery.addEventListener('change', fitCanvas);
window.addEventListener('resize', fitCanvas);
fitCanvas();
const game = new Game(canvas);
const audio = new AudioSystem();
game.start();

// Keep all in-game overlays aligned with the scene, including letterboxed screens.
for (const selector of ['#hud', '#play-controls', '#pause-btn']) {
  stageViewport.appendChild(document.querySelector(selector)!);
}
const toolBelt = document.querySelector('.hud-right')!;
document.querySelector('#app')!.appendChild(toolBelt);
stageViewport.appendChild(document.querySelector('#active-effects')!);

// —— DOM helpers ——
const $ = <T extends HTMLElement>(sel: string): T => {
  const el = document.querySelector<T>(sel);
  if (!el) throw new Error(`Missing element ${sel}`);
  return el;
};

const authPanel = $('#auth-panel');
const menuPanel = $('#menu-panel');
const matchPanel=$('#match-panel');
const chipPanel = $('#chip-panel');
const levelsPanel = $('#levels-panel');
const achPanel = $('#ach-panel');
const guidePanel = $('#guide-panel');
const pausePanel = $('#pause-panel');
const resultPanel = $('#result-panel');
const hud = $('#hud');
const toastEl = $('#toast');

function show(el: HTMLElement, on = true): void {
  el.classList.toggle('hidden', !on);
}

let toastTimer = 0;
function toast(msg: string): void {
  window.clearTimeout(toastTimer);
  toastEl.textContent = msg;
  show(toastEl, true);
  toastTimer = window.setTimeout(() => show(toastEl, false), 2800);
}

// —— Server-backed accounts and cross-device player profile ——
type LocalUser = {
  username: string; email: string; createdAt: number; lastLoginAt: number; unlocked: number;
  stars: Record<string, number>; best: Record<string, number>; achievements: string[]; collection: string[]; hooks: number;
  wallet: number; earnings: Record<string,number>; offered: ChipId[]; owned: ChipId[]; rewardedStages: number[]; checkpoints: Record<string,number>;
  chips: ChipId[]; settings: { muted: boolean; reduced: boolean }; keybindings: unknown;
};
const defaultGuest = (): LocalUser => ({username:'游客矿工',email:'',createdAt:Date.now(),lastLoginAt:Date.now(),unlocked:1,stars:{},best:{},achievements:[],collection:[],hooks:0,wallet:0,earnings:{},offered:[],owned:['magnet','blast'],rewardedStages:[],checkpoints:{},chips:['magnet','blast'],settings:{muted:false,reduced:matchMedia('(prefers-reduced-motion: reduce)').matches},keybindings:null});
let currentUser: LocalUser | null = null;
let guestEntered = false;
let guestProfile = defaultGuest();
try{const saved=JSON.parse(sessionStorage.getItem('mining:guest')||'null');if(saved)guestProfile=applyAccount(saved);}catch{}
if(Array.isArray(guestProfile.keybindings))bindings=guestProfile.keybindings as typeof bindings;
game.setAccountCounters(0,[],kind=>{const profile=currentUser||guestProfile;if(!profile.collection.includes(kind)){profile.collection.push(kind);refreshCollectionCount();void saveCurrentUser();}});
async function api(path:string,method='GET',body?:unknown):Promise<any>{
  const response=await fetch(`/api/${path}`,{method,credentials:'same-origin',headers:body?{'content-type':'application/json'}:undefined,body:body?JSON.stringify(body):undefined});
  const value=await response.json().catch(()=>({error:'账号服务暂不可用，请稍后重试'}));
  if(!response.ok)throw new Error(value.error||'请求失败');return value;
}
function applyAccount(value:any): LocalUser {
  return {username:value.username,email:value.email,createdAt:value.createdAt,lastLoginAt:value.lastLoginAt,unlocked:value.unlocked||1,stars:value.stars||{},best:value.best||{},achievements:value.achievements||[],collection:value.collection||[],hooks:value.hooks||0,wallet:value.wallet||0,earnings:value.earnings||{},offered:value.offered||[],owned:value.owned||['magnet','blast'],rewardedStages:value.rewardedStages||[],checkpoints:value.checkpoints||Object.fromEntries(Array.from({length:value.unlocked||1},(_,i)=>[String(i+1),cumulativeTarget(i)])),chips:value.chips||['magnet','blast'],settings:value.settings||{muted:false,reduced:false},keybindings:value.keybindings||null};
}
let profileSaveQueue=Promise.resolve();
function saveCurrentUser():Promise<void>{
  if(!currentUser){guestProfile.hooks=game.playersStats().hooks;sessionStorage.setItem('mining:guest',JSON.stringify(guestProfile));return Promise.resolve();}
  currentUser.hooks=game.playersStats().hooks;
  const snapshot=structuredClone(currentUser);
  profileSaveQueue=profileSaveQueue.then(async()=>{try{await api('profile','PUT',snapshot);}catch(error){toast(error instanceof Error?error.message:'进度同步失败');}});
  return profileSaveQueue;
}
function bindAccountCounters():void{const profile=currentUser||guestProfile;game.setAccountCounters(profile.hooks,profile.collection,kind=>{const active=currentUser||guestProfile;if(!active.collection.includes(kind)){active.collection.push(kind);refreshCollectionCount();void saveCurrentUser();}});}
function currentSession():string|null{return currentUser?.username||null;}
function findUser(name:string):LocalUser|undefined{return currentUser?.username.toLowerCase()===name.toLowerCase()?currentUser:undefined;}
function loadGuest():LocalUser{return guestProfile;}
const authMsg = $('#auth-msg');
function setAuthMsg(text:string,error=false):void{authMsg.textContent=text;authMsg.classList.toggle('error',error);}

// tabs
document.querySelectorAll<HTMLButtonElement>('[data-auth-tab]').forEach((btn) => {
  btn.addEventListener('click', () => {
    document.querySelectorAll('.tab').forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
    const tab = btn.dataset.authTab;
    show($('#form-login'), tab === 'login');
    show($('#form-register'), tab === 'register');
    show($('#form-forgot'), tab === 'forgot');
    setAuthMsg('');
  });
});

$('#form-login').addEventListener('submit', async (e) => {
  e.preventDefault();const fd=new FormData(e.target as HTMLFormElement);
  try{currentUser=applyAccount((await api('login','POST',{username:String(fd.get('username')||'').trim(),password:String(fd.get('password')||'')})).user);bindAccountCounters();enterMenu(false);audio.ui();}
  catch(error){setAuthMsg(error instanceof Error?error.message:'登录失败',true);}
});
$('#form-register').addEventListener('submit', async (e) => {
  e.preventDefault();const fd=new FormData(e.target as HTMLFormElement),password=String(fd.get('password')||'');
  if(password!==String(fd.get('password2')||'')){setAuthMsg('两次密码不一致',true);return;}
  try{currentUser=applyAccount((await api('register','POST',{username:String(fd.get('username')||'').trim(),email:String(fd.get('email')||'').trim(),password})).user);bindAccountCounters();toast('账号已安全保存到矿谷数据库，欢迎下井！');enterMenu(false);}
  catch(error){setAuthMsg(error instanceof Error?error.message:'注册失败',true);}
});
$('#form-forgot').addEventListener('submit',async e=>{e.preventDefault();try{await api('password-reset','POST',Object.fromEntries(new FormData(e.target as HTMLFormElement)));}catch(error){setAuthMsg(error instanceof Error?error.message:'邮件找回暂不可用',true);}});
$('#guest-btn').addEventListener('click',()=>{guestEntered=true;currentUser=null;bindAccountCounters();enterMenu(true);void saveCurrentUser();});

$('#logout-btn').addEventListener('click', async () => {
  if(currentUser)await api('logout','POST').catch(()=>{});
  currentUser=null;guestProfile=defaultGuest();bindAccountCounters();
  show(menuPanel, false);
  show(authPanel, true);
});

// —— Menu / panels ——
function enterMenu(guest = !currentSession()): void {
  game.returnToMenu();
  $('#app').classList.remove('playing');
  show($('#play-controls'), false);
  show(pausePanel, false);
  show(matchPanel,false);
  show(resultPanel, false);
  show(authPanel, false);
  show(menuPanel, true);
  show(hud, false);
  const name = guest ? '游客矿工' : currentSession() || '矿工';
  $('#welcome-name').textContent = name;
  const user = guest ? null : findUser(name);
  $('#welcome-meta').textContent = user
    ? `邮箱 ${user.email} · 解锁 ${user.unlocked} 关 · 累计最佳 ${Math.max(0,...Object.values(user.best))} · 金币 ${user.wallet}`
    : `访客进度仅保留在本次访问 · 已解锁 ${loadGuest().unlocked} / 50 关 · 累计最佳 ${Math.max(0,...Object.values(loadGuest().best))} · 金币 ${loadGuest().wallet}`;
  if (!(currentUser||guestProfile).owned.includes(primaryChip)) primaryChip = 'magnet';
  if (!(currentUser||guestProfile).owned.includes(secondaryChip) || secondaryChip === primaryChip) secondaryChip = primaryChip === 'blast' ? 'magnet' : 'blast';
  refreshAchCount();
  refreshCollectionCount();
  $('#chip-desc').textContent = `${CHIP_DEFS[primaryChip].name} + ${CHIP_DEFS[secondaryChip].name}`;
}

function openPlay(mode: GameMode, level = 1, options:MatchOptions|null=null): void {
  for (const panel of [authPanel, menuPanel, matchPanel, levelsPanel, chipPanel, achPanel, guidePanel, pausePanel]) show(panel, false);
  $('#app').classList.add('playing');
  fitCanvas();
  show($('#play-controls'), true);
  show(menuPanel, false);
  show(resultPanel, false);
  show(hud, true);
  game.setMode(mode);
  game.configureMatch(mode==='solo'?null:options);
  game.startLevel(level, primaryChip, secondaryChip, mode==='solo'?(currentUser||guestProfile).checkpoints[String(level)]||0:0);
  toast(mode === 'solo' ? `第 ${level} 层 · ${stageForLevel(level).name}` : mode === 'coop' ? '双人合作 · 合力达标' : options?.rule==='race'?'双人目标赛 · 先达标者胜':'双人计时赛 · 到时高分获胜');
}

document.querySelectorAll<HTMLButtonElement>('[data-start]').forEach((btn) => {
  btn.addEventListener('click', () => {
    const mode = btn.dataset.start as GameMode;
    if(mode==='solo')prepareSolo((currentUser||guestProfile).unlocked);else openMatchSettings(mode);
  });
});

document.querySelectorAll<HTMLButtonElement>('[data-back]').forEach((btn) => {
  btn.addEventListener('click', () => {
    show(chipPanel, false);
    show(levelsPanel, false);
    show(achPanel, false);
    show(guidePanel, false);
    show(menuPanel, true);
  });
});

// chips
let primaryChip: ChipId = 'magnet';
let secondaryChip: ChipId = 'blast';
let pendingSoloLevel: number | null = null;
function prepareSolo(level: number): void {
  pendingSoloLevel=level;
  game.returnToMenu();
  for(const panel of [menuPanel,levelsPanel,resultPanel,hud,pausePanel])show(panel,false);
  $('#app').classList.remove('playing');
  show($('#play-controls'),false);
  hideToolTooltip();
  show(chipPanel,true);
  $('#chip-confirm').textContent=`带上背包 · 开始第 ${level} 关`;
  renderChipGrid();
  chipPanel.scrollTop=0;
  chipPanel.querySelector('.panel')!.scrollTop=0;
}
let lastMatch:{mode:GameMode;level:number;options:MatchOptions}|null=null;
$('#match-level').innerHTML=LEVEL_NAMES.map((name,index)=>`<option value="${index+1}">第 ${index+1} 层 · ${name}</option>`).join('');
function updateMatchSettings():void {
  const mode=$<HTMLSelectElement>('#match-mode').value,level=Number($<HTMLSelectElement>('#match-level').value),stage=stageForLevel(level);
  document.querySelectorAll<HTMLElement>('.versus-setting').forEach(el=>show(el,mode==='versus'));
  const duration=$<HTMLSelectElement>('#match-duration');duration.options[0].disabled=mode==='versus';if(mode==='versus'&&duration.value==='0')duration.value='90';
  $('#match-rule-hint').textContent=mode==='coop'?'合力达到关卡目标；阶段工具仍由各自独立使用。':'约定时间内同场竞争。时间类奖励按每秒 20 金币折算，不延长比赛；矿石减少后出现新的矿脉或可抓取魔法道具。';
  const ids=(Object.keys(CHIP_DEFS) as ChipId[]).filter(id=>CHIP_DEFS[id].stage<=stage.number);
  for(const [index,player] of ['p1','p2'].entries()){
    for(const slot of ['primary','secondary']){
      const select=$<HTMLSelectElement>(`#match-${player}-${slot}`),old=select.value;
      select.innerHTML=ids.map(id=>`<option value="${id}">${CHIP_DEFS[id].name}</option>`).join('');
      const fallback=slot==='primary'?(index===0?primaryChip:'twinHook'):(index===0?secondaryChip:'rescueBell');
      select.value=ids.includes(old as ChipId)?old:ids.includes(fallback as ChipId)?fallback:slot==='primary'?'magnet':'blast';
    }
    $(`#match-${player}-field`).textContent=`随行道具：${CHIP_DEFS[stage.fieldTool].name} · ${index===0?'R':'I'}`;
  }
  $('#match-error').textContent='';
}
function openMatchSettings(mode:GameMode):void {
  show(menuPanel,false);show(matchPanel,true);$<HTMLSelectElement>('#match-mode').value=mode;
  if(lastMatch){$<HTMLSelectElement>('#match-level').value=String(lastMatch.level);$<HTMLSelectElement>('#match-duration').value=String(lastMatch.options.duration);}
  else $<HTMLSelectElement>('#match-duration').value=mode==='versus'?'90':'0';
  updateMatchSettings();
}
$('#match-mode').addEventListener('change',updateMatchSettings);$('#match-level').addEventListener('change',updateMatchSettings);
$('#match-back').addEventListener('click',()=>{show(matchPanel,false);show(menuPanel,true)});
$('#match-form').addEventListener('submit',event=>{
  event.preventDefault();const value=(id:string)=>$<HTMLSelectElement>(`#match-${id}`).value;
  const loadouts=[['p1','primary','secondary'],['p2','primary','secondary']].map(([player,a,b])=>[value(`${player}-${a}`),value(`${player}-${b}`)] as [ChipId,ChipId]) as [[ChipId,ChipId],[ChipId,ChipId]];
  if(loadouts.some(([a,b])=>a===b)){$('#match-error').textContent='每位矿工请选择两件不同的装备。';return;}
  const mode=value('mode') as GameMode,level=Number(value('level'));
  const options:MatchOptions={duration:Number(value('duration')),rule:value('rule') as MatchOptions['rule'],refill:value('refill') as MatchOptions['refill'],loadouts};
  lastMatch={mode,level,options};openPlay(mode,level,options);
});
const chipGrid = $('#chip-grid');

function renderChipGrid(): void {
  const profile=currentUser||guestProfile;
  $('#tool-unlock-note').textContent = `金币余额 ${profile.wallet} · 每完成一个场景，随机上架最多 3 件工具；购买后永久拥有，点击选择主副道具。`;
  chipGrid.innerHTML = '';
  (Object.keys(CHIP_DEFS) as ChipId[]).forEach((id) => {
    const def = CHIP_DEFS[id];
    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'chip-card';
    card.dataset.chip=id;
    const owned=profile.owned.includes(id);
    const price=300+def.stage*100;
    const locked = !owned && !profile.offered.includes(id);
    card.disabled = locked || (!owned && profile.wallet<price);
    card.setAttribute('aria-pressed', String(id === primaryChip || id === secondaryChip));
    if (id === primaryChip) card.classList.add('selected-primary');
    if (id === secondaryChip) card.classList.add('selected-secondary');
    card.innerHTML = `<strong>${id === primaryChip ? '[主] ' : id === secondaryChip ? '[副] ' : ''}${def.name}</strong>${toolIcon(id)}<span>${def.desc}<br/>${locked ? `完成场景后随机上架` : !owned ? `购买：${price} 金币` : def.cooldown >= 999 ? '每层一次' : `冷却 ${def.cooldown} 秒`}</span>`;
    card.addEventListener('click', () => {
      if(!owned){profile.wallet-=price;profile.owned.push(id);toast(`已购买 ${def.name}`);}
      if (id === primaryChip) return;

      // click assigns: if not selected, make secondary then swap on second click path
      if (primaryChip !== id) {
        secondaryChip = primaryChip;
        primaryChip = id;
      }
      (currentUser||guestProfile).chips=[primaryChip,secondaryChip];void saveCurrentUser();
      renderChipGrid();
      $('#chip-desc').textContent = `${CHIP_DEFS[primaryChip].name} + ${CHIP_DEFS[secondaryChip].name}`;
      audio.ui();
    });
    chipGrid.appendChild(card);
  });
}

$('#chip-select-btn').addEventListener('click', () => {
  pendingSoloLevel=null;
  $('#chip-confirm').textContent='确认背包';
  show(menuPanel, false);
  show(chipPanel, true);
  renderChipGrid();
});
$('#chip-confirm').addEventListener('click', () => {
  if(pendingSoloLevel!==null){const level=pendingSoloLevel;pendingSoloLevel=null;openPlay('solo',level);return;}
  show(chipPanel, false);
  show(menuPanel, true);
  $('#chip-desc').textContent = `${CHIP_DEFS[primaryChip].name} + ${CHIP_DEFS[secondaryChip].name}`;
});

// levels
const levelGrid = $('#level-grid');
function renderLevels(): void {
  levelGrid.innerHTML = '';
  const name = currentSession();
  const user = name ? findUser(name) : undefined;
  const progress = user ?? loadGuest();
  const unlocked = progress.unlocked;
  for (const stage of STAGES) {
    const section = document.createElement('section'); section.className = 'stage-map-section'; section.dataset.stage = stage.id;
    section.innerHTML = `<div class="stage-map-head"><img src="${stage.background}" alt="${stage.name}风景" loading="eager"/><div><span class="eyebrow">第 ${stage.number} 站 · 第 ${stage.levels[0]}–${stage.levels[stage.levels.length-1]} 层</span><h3>${stage.name}</h3><p>${stage.description}</p><p class="muted">特色：${stage.signature.map((id)=>MINERAL_DEFS[id].label).join('、')} · 随行：${CHIP_DEFS[stage.fieldTool].name}</p></div></div>`;
    const pair = document.createElement('div'); pair.className = 'stage-level-pair';
    section.appendChild(pair);
    for (const i of stage.levels) {
    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'level-card' + (i > unlocked ? ' locked' : '');
    card.disabled = i > unlocked;
    const stars = progress.stars?.[String(i)] ?? 0;
    card.innerHTML = `<strong>第 ${i} 层 · ${LEVEL_NAMES[i - 1]}</strong><div class="stars">${'★'.repeat(stars)}${'☆'.repeat(3 - stars)}</div><div class="muted">${cumulativeTarget(i)} 累计金币 · ${levelCurve(i).time} 秒</div><div class="muted">${levelCurve(i).hazards} 障碍 · ${levelCurve(i).creatures} 生物</div>`;
    card.addEventListener('click', () => {
      if (i > unlocked) return;
      prepareSolo(i);
    });
    pair.appendChild(card);
    }
    const recommend = document.createElement('button'); recommend.className = 'btn sm stage-recommend'; recommend.disabled = stage.levels[0] > unlocked || !stage.recommended.every(id=>progress.owned.includes(id));
    recommend.textContent = `装配推荐：${stage.recommended.map((id)=>CHIP_DEFS[id].name).join(' + ')}`;
    recommend.addEventListener('click', () => {
      [primaryChip, secondaryChip] = stage.recommended;
      (currentUser||guestProfile).chips=[primaryChip,secondaryChip];void saveCurrentUser();
      $('#chip-desc').textContent = `${CHIP_DEFS[primaryChip].name} + ${CHIP_DEFS[secondaryChip].name}`;
      toast(`已装配 ${stage.name} 推荐背包`);
    });
    section.appendChild(recommend); levelGrid.appendChild(section);
  }
}
$('#levels-btn').addEventListener('click', () => {
  show(menuPanel, false);
  show(levelsPanel, true);
  renderLevels();
});

function collection(): Set<MineralKind> { return new Set((currentUser||guestProfile).collection.filter((id):id is MineralKind=>id in MINERAL_DEFS)); }
function refreshCollectionCount(): void { $('#collection-count').textContent = `${collection().size} / ${Object.keys(MINERAL_DEFS).length}`; }
let guideStage = 0;
function renderGuide(): void {
  const tabs = $('#guide-tabs'); tabs.innerHTML = '';
  for (const [index,label] of ['全部矿物',...STAGES.map((s)=>s.name)].entries()) {
    const button = document.createElement('button'); button.className = 'tab' + (guideStage===index?' active':''); button.textContent = label;
    button.addEventListener('click', () => { guideStage=index;renderGuide(); }); tabs.appendChild(button);
  }
  const selected = guideStage > 0 ? STAGES[guideStage-1] : null;
  $('#guide-stage').innerHTML = selected ? `<img src="${selected.background}" alt="${selected.name}风景"/><div><h3>${selected.name}</h3><p>${selected.description}</p><p>${selected.tip}</p><p>随行工具：${CHIP_DEFS[selected.fieldTool].name}。${CHIP_DEFS[selected.fieldTool].desc}</p></div>` : '<p class="hint">高价尖晶更小，大块矿物通常更重。观察收益和重量再选路线；同色连抓有加成，挡路岩石可先搬走或炸碎。</p>';
  $('#creature-guide').innerHTML=(selected?selected.creatures:Object.keys(CREATURE_DEFS) as CreatureKind[]).map(kind=>`<article class="creature-card">${creatureIcon(kind,64)}<h3>${CREATURE_DEFS[kind].name}</h3><p>${CREATURE_DEFS[kind].description}</p></article>`).join('');
  const list = $('#mineral-guide'); list.innerHTML='';const found=collection();
  for (const id of Object.keys(MINERAL_DEFS) as MineralKind[]) {
    const def=MINERAL_DEFS[id];
    if (selected && !selected.signature.includes(id)) continue;
    const item = document.createElement('article');item.className='mineral-card' + (found.has(id)?' discovered':'');item.dataset.mineral=id;
    item.innerHTML=`${mineralIcon(id,56)}<h3>${def.label}</h3><span class="discovery-status">${found.has(id)?'已发现':'等待初见'}</span><p>${def.value} 金币 · 重量 ${def.weight} · ${def.radius<=.34?'细小需精准':def.radius>=.75?'大块易命中':'中等尺寸'}</p><p class="muted">${def.description || (id==='dynamite'?`会扣除 ${DYNAMITE_PENALTY} 金币；护盾可以抵挡扣款。`:def.combo==='none'?'不参与连击，可能中断当前连击。':'连续抓取同色矿物，获得更高收益。')}</p>`;
    list.appendChild(item);
  }
}
$('#field-guide-btn').addEventListener('click', () => {show(menuPanel,false);show(guidePanel,true);renderGuide();});

// achievements demo catalog (same as DB seed subset)
const ACH = [
  ['first_gold', '初见金光', '第一次抓取金粒', 'bronze'],
  ['hook_100', '百钩成钢', '累计出钩 100 次', 'bronze'],
  ['rich_1k', '首个千元', '单关金额 ≥ 1000', 'bronze'],
  ['combo_x3', '共鸣初现', '普通连击倍率达到 ×1.65', 'silver'],
  ['coop_clear', '战友', '完成 1 次双人合作关卡', 'bronze'],
  ['versus_win', '矿脉之王', '竞技获胜 1 次', 'bronze'],
  ['account_secure', '矿证在手', '成功修改密码', 'bronze'],
  ['stage_1', '叶影书签', '完成叶影林间的两层旅程', 'bronze'],
  ['stage_2', '薄荷书签', '完成薄荷晶洞的两层旅程', 'silver'],
  ['stage_3', '蘑菇书签', '完成蘑菇花谷的两层旅程', 'silver'],
  ['stage_4', '古树书签', '完成古树遗迹的两层旅程', 'silver'],
  ['stage_5', '月光书签', '完成月光矿湖的两层旅程', 'legend'],
  ['level_10', '星辉归途', '通关全部 50 关', 'legend'],
] as const;



function loadAchDone(): Set<string> { return new Set((currentUser||guestProfile).achievements); }

function markAch(code: string, title: string): void {
  const done = loadAchDone();
  if (done.has(code)) return;
  done.add(code);
  (currentUser||guestProfile).achievements=[...done];void saveCurrentUser();
  toast(`成就解锁 · ${title}`);
  refreshAchCount();
}

function refreshAchCount(): void {
  const n = loadAchDone().size;
  $('#ach-count').textContent = `${n} / ${ACH.length}`;
}

function renderAch(): void {
  const list = $('#ach-list');
  list.innerHTML = '';
  const done = loadAchDone();
  for (const [code, name, desc, tier] of ACH) {
    const item = document.createElement('div');
    item.className = 'ach-item' + (done.has(code) ? ' done' : '');
    item.innerHTML = `<div><strong>${name}</strong><div class="muted">${desc}</div></div><div class="tier">${({bronze: '铜章', silver: '银章', legend: '珍藏'})[tier]}</div>`;
    list.appendChild(item);
  }
}

$('#achievements-btn').addEventListener('click', () => {
  show(menuPanel, false);
  show(achPanel, true);
  renderAch();
});

// pause / result
$('#resume-btn').addEventListener('click', () => {
  game.pause();
  show(pausePanel, false);
  $('#pause-btn').focus();
});
$('#restart-btn').addEventListener('click', () => {
  show(pausePanel, false);
  const s = game.getSummary();
  game.startLevel(s.level, primaryChip, secondaryChip, s.mode==='solo'?(currentUser||guestProfile).checkpoints[String(s.level)]||0:0);
});
$('#quit-btn').addEventListener('click', () => {
  void saveCurrentUser();
  show(pausePanel, false);
  show(hud, false);
  enterMenu();
});

$('#result-continue').addEventListener('click', () => {
  const s = game.getSummary();
  show(resultPanel, false);
  if (s.mode === 'solo' && s.state === 'level-clear' && s.level < 50) {
    prepareSolo(s.level+1);
  } else {
    if(s.mode==='solo')prepareSolo(s.state==='game-over'?s.level:1);else openPlay(s.mode,s.level,lastMatch?.options??null);
  }
});
$('#result-retry').addEventListener('click', () => {
  const s = game.getSummary();
  show(resultPanel, false);
  game.startLevel(s.level, primaryChip, secondaryChip, s.mode==='solo'?(currentUser||guestProfile).checkpoints[String(s.level)]||0:0);
});
$('#result-menu').addEventListener('click', () => {
  show(resultPanel, false);
  show(hud, false);
  enterMenu();
});

// chips in HUD
function onChipClick(player:number,slot: ChipSlotName): void {
  if(!game.visualsReady)return;
  const id = game.activateChip(player, slot);
  if(id&&!reduced){const button=$(`#chip-p${player+1}-${slot==='primary'?'p':slot==='secondary'?'s':'f'}`);button.classList.remove('cast');void button.offsetWidth;button.classList.add('cast');button.addEventListener('animationend',()=>button.classList.remove('cast'),{once:true});}
}
for(const index of [0,1])for(const slot of ['primary','secondary','field'] as const){
  const key=keyLabel(bindings[index][slot]);
  const suffix=slot==='primary'?'p':slot==='secondary'?'s':'f',button=$(`#chip-p${index+1}-${suffix}`);
  button.dataset.shortcut=key;button.dataset.player=String(index+1);button.dataset.slot=slot;button.removeAttribute('title');
  button.addEventListener('click',()=>{onChipClick(index,slot as ChipSlotName);if(mobileQuery.matches)hideToolTooltip();});
}
const toolTooltip=$('#tool-tooltip');
document.body.appendChild(toolTooltip);
let hoveredTool:HTMLButtonElement|null=null;
function showToolTooltip(button:HTMLButtonElement):void {
  const id=button.dataset.toolId as ChipId;if(!id||!(id in CHIP_DEFS))return;
  const def=CHIP_DEFS[id],competitive=game.getMode()==='versus';
  const description=id==='pocketClock'&&competitive?'本局一次，立即获得 120 金币；竞技时长保持不变。':def.desc;
  const hud=game.getPlayerHud(Number(button.dataset.player)-1);
  toolTooltip.innerHTML=`<header>${toolIcon(id,40)}<div><strong>${def.name}</strong><span>P${button.dataset.player} · ${keyLabel(bindings[Number(button.dataset.player)-1][button.dataset.slot as ChipSlotName])}</span></div></header><p>${description}</p><p class="hint">${def.cooldown>=999?'本局一次':`冷却 ${def.cooldown} 秒`}${button.classList.contains('is-active')?' · 效果正在生效':''}${id==='surveyLens'&&hud?.prediction?`<br/>${hud.prediction}`:''}</p>`;
  show(toolTooltip,true);const rect=button.getBoundingClientRect(),width=toolTooltip.offsetWidth;
  toolTooltip.style.left=`${Math.max(8,Math.min(innerWidth-width-8,rect.left+rect.width/2-width/2))}px`;
  toolTooltip.style.top=`${Math.min(innerHeight-toolTooltip.offsetHeight-8,rect.bottom+8)}px`;
  button.setAttribute('aria-describedby','tool-tooltip');
}
function hideToolTooltip():void {hoveredTool?.removeAttribute('aria-describedby');hoveredTool=null;show(toolTooltip,false);}
for(const button of document.querySelectorAll<HTMLButtonElement>('.chip-btn')){
  button.addEventListener('pointerenter',()=>{hoveredTool=button;showToolTooltip(button)});
  button.addEventListener('pointerleave',hideToolTooltip);
  button.addEventListener('focus',()=>{hoveredTool=button;showToolTooltip(button)});
  button.addEventListener('blur',hideToolTooltip);
}

// —— input ——
function togglePause(): void {
  void saveCurrentUser();
  if (game.getState() === 'active-play' || game.getState() === 'paused') {
    hideToolTooltip();
    game.pause();
    show(pausePanel, game.isPaused());
    if (game.isPaused()) $('#resume-btn').focus();
    else $('#pause-btn').focus();
  }
}
$('#pause-btn').addEventListener('click', togglePause);
$('#fire-p1').addEventListener('click', () => game.fire(0));
$('#fire-p2').addEventListener('click', () => game.fire(1));
window.addEventListener('keydown', (e) => {
  if (game.isPaused() && e.code === 'Tab') {
    const buttons = [...pausePanel.querySelectorAll<HTMLElement>('button:not(:disabled), select:not(:disabled)')];
    const first = buttons[0], last = buttons[buttons.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    return;
  }
  if ((e.target as HTMLElement).closest('input, textarea, select') || e.repeat) return;
  if (e.code === 'Escape') { togglePause(); return; }
  if (game.getState() !== 'active-play') return;
  if (bindings.some(row => Object.values(row).includes(e.code))) e.preventDefault();
  if (!game.visualsReady) return;
  for (const index of [0,1]) for (const action of ACTIONS) if (e.code === bindings[index][action]) {
    if (action === 'fire') game.fire(index); else onChipClick(index, action);
  }
});
canvas.addEventListener('pointerdown', (event) => {
  if (!game.visualsReady || game.getState() !== 'active-play') return;
  const rect = canvas!.getBoundingClientRect();
  const rotated = $('#app').dataset.rotated === 'true' && mobileQuery.matches;
  const position = rotated ? (event.clientY - rect.top) / rect.height : (event.clientX - rect.left) / rect.width;
  const player = mobileQuery.matches && game.getMode() !== 'solo' && position >= .5 ? 1 : 0;
  game.fire(player);
});
document.addEventListener('visibilitychange', () => {
  if (document.hidden && game.getState() === 'active-play') togglePause();
});
let muted = (currentUser||guestProfile).settings.muted;
let reduced = (currentUser||guestProfile).settings.reduced;
function applySettings(): void {
  AudioSystem.muted = muted;
  game.setReducedMotion(reduced);
  $('#app').dataset.reduced=String(reduced);
  $('#sound-btn').textContent = muted ? '♪ 音效关' : '♪ 音效开';
  $('#sound-btn').setAttribute('aria-pressed', String(muted));
  $('#motion-btn').textContent = reduced ? '动态关' : '动态开';
  $('#motion-btn').setAttribute('aria-pressed', String(reduced));
}
$('#sound-btn').addEventListener('click', () => { muted = !muted;(currentUser||guestProfile).settings.muted=muted;void saveCurrentUser();applySettings(); });
$('#motion-btn').addEventListener('click', () => { reduced = !reduced;(currentUser||guestProfile).settings.reduced=reduced;void saveCurrentUser();applySettings(); });
applySettings();

const actionNames = { fire: '出钩', primary: '主道具', secondary: '副道具', field: '随行道具' };
function renderBindings(): void {
  $('#keybinding-grid').innerHTML = [0,1].map(index => `<fieldset><legend>P${index+1}</legend>${ACTIONS.map(action => `<label>${actionNames[action]}<select data-player="${index}" data-action="${action}" aria-label="P${index+1} ${actionNames[action]}键位">${KEY_OPTIONS.map(code => `<option value="${code}" ${bindings[index][action]===code?'selected':''}>${keyLabel(code)}</option>`).join('')}</select></label>`).join('')}</fieldset>`).join('');
  $('#binding-summary').textContent = [0,1].map(index => `P${index+1}：${ACTIONS.map(action => `${keyLabel(bindings[index][action])} ${actionNames[action]}`).join(' · ')}`).join(' / ');
}
$('#keybinding-grid').addEventListener('change', event => {
  const select = event.target as HTMLSelectElement;
  const player = Number(select.dataset.player), action = select.dataset.action as typeof ACTIONS[number];
  if (bindings.some((row,index) => ACTIONS.some(other => !(index===player && other===action) && row[other]===select.value))) {
    $('#binding-status').textContent = '该按键已被使用，请选择其他按键。'; renderBindings(); return;
  }
  bindings[player][action] = select.value;
  (currentUser||guestProfile).keybindings=bindings;void saveCurrentUser();
  $('#binding-status').textContent = '键位已保存。'; renderBindings();
});
$('#reset-bindings').addEventListener('click', () => {
  bindings = structuredClone(DEFAULT_BINDINGS);(currentUser||guestProfile).keybindings=bindings;void saveCurrentUser();renderBindings();
  $('#binding-status').textContent = '已恢复默认键位。';
});
if(!import.meta.env.VITE_MINITOOL){
  $('#fullscreen-btn').addEventListener('click', async () => {
    try {
      if (!document.fullscreenElement) await $('#app').requestFullscreen();
      const orientation = screen.orientation as ScreenOrientation & { lock?: (mode: string) => Promise<void> };
      await orientation.lock?.('landscape');
      $('#fullscreen-status').textContent = '已进入全屏；若设备未旋转，可手动横持。';
    } catch { $('#fullscreen-status').textContent = '当前浏览器不支持全屏横屏，已使用横向游戏布局。'; }
    fitCanvas();
  });
  document.addEventListener('fullscreenchange', fitCanvas);
}
renderBindings();

// HUD sync loop
function syncTools(index:number,p:NonNullable<ReturnType<Game['getPlayerHud']>>):void {
  for(const [slot,id,cd,available] of [['primary',p.chipPId,p.chipPCooldown,p.chipPAvailable],['secondary',p.chipSId,p.chipSCooldown,p.chipSAvailable],['field',p.chipFId,p.chipFCooldown,p.chipFAvailable]] as const){
    const suffix=slot==='primary'?'p':slot==='secondary'?'s':'f',button=$<HTMLButtonElement>(`#chip-p${index+1}-${suffix}`),key=keyLabel(bindings[index][slot]),def=CHIP_DEFS[id];
    if(button.dataset.toolId!==id){button.innerHTML=`${toolIcon(id,40)}<kbd class="tool-key">${key}</kbd><span class="tool-cooldown"></span>`;button.dataset.toolId=id;}
    button.querySelector('.tool-key')!.textContent=key;
    button.dataset.shortcut=key;
    button.querySelector('.tool-cooldown')!.textContent=!available?'✓':cd>0?`${Math.ceil(cd)}s`:'';
    button.disabled=!game.visualsReady||!available||cd>0;button.classList.toggle('used',!available);button.classList.toggle('is-active',p.activeTools.includes(id));
    button.setAttribute('aria-label',`P${index+1} ${def.name}，快捷键 ${key}。${id==='pocketClock'&&game.getMode()==='versus'?'本局一次，立即获得120金币，不延长比赛。':def.desc}${!available?'，本局已用':cd>0?`，冷却 ${Math.ceil(cd)} 秒`:''}`);
  }
}
function renderScoreHelp(player:number|null):void {
  const s=game.getSummary(),stage=stageForLevel(s.level);
  const stats=player===null?s.roundStats.reduce((sum,r)=>({treasures:sum.treasures+r.treasures,obstacles:sum.obstacles+r.obstacles,empty:sum.empty+r.empty,heavy:sum.heavy+r.heavy,tools:sum.tools+r.tools,pickups:sum.pickups+r.pickups}),{treasures:0,obstacles:0,empty:0,heavy:0,tools:0,pickups:0}):s.roundStats[player];
  const tips:string[]=[];
  if(stats.empty>2)tips.push('空钩较多：等绳索指向宝物中心再出钩，优先近处大矿练准方向。');
  if(stats.obstacles>0)tips.push('岩石和炸药拖慢了收益：用碎石火花清路，护盾避开炸药扣款。');
  if(stats.heavy>1)tips.push('重矿搬运耗时：搭配轻巧绞盘或羽毛祝福，不划算的重物可剪绳放回。');
  if(stats.tools===0)tips.push('本局没有使用装备：P1 用 Q/E/R，P2 用 O/P/I，适时释放能节省收钩时间。');
  tips.push('连续抓取同色宝物可累积到 ×1.65。优先选价值高、较轻且路线无遮挡的矿物。');
  if(s.mode==='versus')tips.push('新矿脉中的金圈物品是免费魔法补给；抓回即可生效，不消耗装备次数。');
  tips.push(stage.tip);
  $('#result-help').innerHTML=`<h3>${player===null?'下次如何获取更高分':`给 P${player+1} 的高分建议`}</h3><p class="round-facts">宝物 ${stats.treasures} · 空钩 ${stats.empty} · 障碍 ${stats.obstacles} · 装备释放 ${stats.tools}</p><ul>${tips.slice(0,4).map(t=>`<li>${t}</li>`).join('')}</ul><div class="help-loadout">${stage.recommended.map(id=>toolIcon(id,34)).join('')}<span>推荐：${stage.recommended.map(id=>CHIP_DEFS[id].name).join(' + ')}</span></div>`;
}
let lastHudUpdate = 0;
function syncHud(time: number): void {
  requestAnimationFrame(syncHud);
  if (time - lastHudUpdate < 100) return;
  lastHudUpdate = time;
  const s = game.getSummary();
  const p1 = game.getPlayerHud(0);
  const p2 = game.getPlayerHud(1);
  $('#level-label').textContent = `第 ${s.level} 层`;
  const stage = stageForLevel(s.level);
  $('#level-label').title = LEVEL_NAMES[s.level - 1];
  $('#app').dataset.stage = stage.id;
  $('#app').dataset.multiplayer = String(!!p2);
  $('#app').style.setProperty('--stage-color', stage.color);
  $('#stage-name').textContent = stage.name;
  $('#stage-label').textContent = `第 ${stage.number} / 5 站`;
  $('#stage-tip').textContent = stage.tip;
  $('#target-label').textContent=s.mode==='versus'&&s.rule==='timed'?'参考分':'目标';
  $('#risk-status').textContent=game.visualsReady?(s.threat.includes('盯上')?s.threat:''):game.visualLoadError?'素材加载失败，请刷新页面；计时已暂停':'素材加载中，加载完成后开始计时…';
  $<HTMLButtonElement>('#fire-p1').disabled=!game.visualsReady;
  $<HTMLButtonElement>('#fire-p2').disabled=!game.visualsReady;
  $('#board-budget').textContent=`剩余宝物 ${s.remainingPotential} / 初始 ${s.potential} · 矿物加时 ${s.mineralTimeUsed}/${MINERAL_TIME_CAP}s`;
  $('#active-effects').textContent = [p1?.effects ? `P1 ${p1.effects}` : '', p2?.effects ? `P2 ${p2.effects}` : '', p1?.prediction ? `探脉：${p1.prediction}` : '', p2?.prediction ? `P2 探脉：${p2.prediction}` : '', s.backgroundStatus === 'error' ? '背景暂未加载成功，请刷新页面' : ''].filter(Boolean).join(' | ');
  const total = s.mode === 'versus' ? Math.max(...s.scores) : s.scores.reduce((a, b) => a + b, 0);
  const percent = Math.min(100, Math.round(total / s.target * 100));
  ($('#goal-progress') as HTMLProgressElement).value = percent;
  $('#progress-value').textContent = `${percent}%`;
  $('#fire-p2').classList.toggle('hidden', !p2);
  for (const index of [0,1]) $(`#fire-p${index+1}`).textContent = `↓ P${index+1} · ${keyLabel(bindings[index].fire)}`;
  $('#target-label').textContent=s.mode==='solo'?'累计目标':'目标';
  $('#score-p1').textContent = String(p1?.money ?? 0);
  $('#score-p2').textContent = String(p2?.money ?? 0);
  $('#play-hint').textContent = s.mode === 'versus' ? `P1 ${s.scores[0]} · P2 ${s.scores[1]} · ${s.rule==='timed'?'到时高分获胜':'先达目标获胜'}` : `${stage.signature.map((id)=>`${MINERAL_DEFS[id].label} ${MINERAL_DEFS[id].value}`).join(' · ')} · 同色连抓有加成`;
  if (s.state === 'active-play' || s.state === 'level-clear') {
    if (game.playersStats().foundGold) markAch('first_gold', '初见金光');
    if (game.playersStats().hooks >= 100) markAch('hook_100', '百钩成钢');
    if (game.playersStats().combo >= 1.65) markAch('combo_x3', '共鸣初现');
  }
  if (p1) {
    $('#target-value').textContent = String(s.target);
    $('#score-value').textContent = s.mode === 'versus' ? `${p1.money} / ${p2?.money ?? 0}` : String(total);
    $('#timer-value').textContent = String(Math.ceil(s.timeLeft));
    $('#combo-value').textContent = p1.combo || '—';
    syncTools(0,p1);
  }
  $('#chip-row-p2').hidden = !p2;
  if (p2) {
    syncTools(1,p2);
  }
  if(hoveredTool&&!toolTooltip.classList.contains('hidden'))showToolTooltip(hoveredTool);

  const banner = $('#event-banner');
  const evName: Record<string, string> = {
    goldRain: '金雨降临！',
    caveIn: '塌方落石！',
    blackout: '全井断电…',
    veinShift: '矿脉涌动',
    gopher: '淘金地鼠出没！',
  };
  banner.textContent = s.refill || evName[s.event] || (s.threat.includes('盯上')?'鼹鼠正在瞄准矿物':'' );

  if (s.state === 'level-clear' && resultPanel.classList.contains('hidden')) {
    show(resultPanel, true);
    hideToolTooltip();
    $('#result-title').textContent = s.mode === 'versus' ? s.winner===null?'平分秋色 · 平局':`P${s.winner+1} 获胜` : s.level === 50 ? '矿谷旅程圆满完成！' : '今天也满载而归';
    show($('#result-help'),s.mode==='versus');if(s.mode==='versus')renderScoreHelp(s.winner===null?null:1-s.winner);
    $('#result-continue').textContent = s.mode === 'solo' && s.level < 50 ? '下一段旅程' : '再玩一次';
    $('#result-detail').textContent = `金额 ${s.scores.join(' / ')} · 目标 ${s.target} · 初始宝物 ${s.potential}` + (s.mode === 'solo' && s.level % 10 === 0 ? `。${stage.name}的旅程完成，${s.level < 50 ? `下一站：${stageForLevel(s.level + 1).name}。` : '欢迎带着星光回家。'}` : '');
    const name = currentSession();
    const user = name ? findUser(name) : loadGuest();
    if (user && s.mode === 'solo') {
      const baseline=user.checkpoints[String(s.level)]||0;
      const earnings=Math.max(0,(s.scores[0]||0)-baseline);
      const previous=user.earnings[String(s.level)]??Math.max(0,(user.best[String(s.level)]||0)-baseline);
      user.wallet+=Math.max(0,earnings-previous);
      user.earnings[String(s.level)]=Math.max(previous,earnings);
      if(s.level<50)user.checkpoints[String(s.level+1)]=Math.max(user.checkpoints[String(s.level+1)]||0,s.scores[0]||0);
      if(s.level%10===0&&!user.rewardedStages.includes(stage.number)){
        user.rewardedStages.push(stage.number);
        const pool=(Object.keys(CHIP_DEFS) as ChipId[]).filter(id=>!user.owned.includes(id)&&!user.offered.includes(id));
        const added=drawStageOffers(pool,STAGES.length-stage.number);
        user.offered.push(...added);
        toast(added.length?`商店新上架：${added.map(id=>CHIP_DEFS[id].name).join('、')}`:'全部工具已在商店上架');
      }
      user.best[String(s.level)] = Math.max(user.best[String(s.level)] || 0, s.scores[0] || 0);
      user.unlocked = Math.max(user.unlocked, Math.min(50, s.level + 1));
      user.stars[String(s.level)] = Math.min(3, Math.max(user.stars[String(s.level)] || 0, s.timeLeft >= levelCurve(s.level).time * 0.65 ? 3 : s.timeLeft >= levelCurve(s.level).time * 0.30 ? 2 : 1));
      if (user.stars[String(stage.levels[0])] && user.stars[String(stage.levels[stage.levels.length-1])]) markAch(`stage_${stage.number}`, `${stage.name}书签`);
    }
    if ((s.scores[0] || 0) >= 1000) markAch('rich_1k', '首个千元');
    if (s.mode === 'coop') markAch('coop_clear', '战友');
    if (s.mode === 'versus'&&s.winner!==null) markAch('versus_win', '矿脉之王');
    if (s.mode === 'solo' && s.level >= 50) markAch('level_10', '深渊行者');
    void saveCurrentUser();
  }
  if (s.state === 'game-over' && resultPanel.classList.contains('hidden')) {
    hideToolTooltip();show($('#result-help'),true);renderScoreHelp(null);
    show(resultPanel, true);
    $('#result-title').textContent = '休息一下，再出发';
    $('#result-continue').textContent = '重新出发';
    $('#result-detail').textContent = `还差 ${Math.max(0, s.target - total)} 金币。下次试试：${stage.tip}`;
  }

}
requestAnimationFrame(syncHud);

// Restore the server session, then migrate all old browser accounts over HTTPS.
function legacyKeys():string[]{return Array.from({length:localStorage.length},(_,i)=>localStorage.key(i)).filter((key):key is string=>!!key&&key.startsWith('gmn:'));}
function legacyProfile(username:string,legacy:any):any{
  const read=(key:string,fallback:any)=>{try{return JSON.parse(localStorage.getItem(key)||JSON.stringify(fallback));}catch{return fallback;}};
  return {unlocked:legacy.unlocked,stars:legacy.stars,best:legacy.best,hooks:Number(localStorage.getItem(`gmn:hooks:${username}`)||0),achievements:read(`gmn:ach:${username}`,[]),collection:read(`gmn:collection:${username}`,[]),chips:read('gmn:chips',['magnet','blast']),settings:{muted:localStorage.getItem('gmn:muted')==='1',reduced:localStorage.getItem('gmn:reduced')==='1'},keybindings:read('gmn:keybindings',null)};
}
void (async()=>{
  try{const restored=(await api('me')).user;if(guestEntered)return;currentUser=applyAccount(restored);}catch{if(guestEntered)return;currentUser=null;}
  try{
    const stored=JSON.parse(localStorage.getItem('gmn:users')||'[]'),legacyUsers=Array.isArray(stored)?stored:[],activeName=localStorage.getItem('gmn:session');
    if(!currentUser){const guest=JSON.parse(localStorage.getItem('gmn:guestProgress')||'null');if(guest&&typeof guest.unlocked==='number')guestProfile={...defaultGuest(),...guest,achievements:JSON.parse(localStorage.getItem('gmn:ach:guest')||'[]'),collection:JSON.parse(localStorage.getItem('gmn:collection:guest')||'[]')};}
    let imported=0;
    for(const legacy of legacyUsers){
      if(!legacy?.username||!legacy?.email||!legacy?.password||!legacy?.stars||!legacy?.best)continue;
      const login=!currentUser&&legacy.username===activeName;
      const result=await api('migrate-local','POST',{username:legacy.username,email:legacy.email,password:legacy.password,profile:legacyProfile(legacy.username,legacy),login});
      imported++;
      if(login)currentUser=applyAccount(result.user);
    }
    if(imported===legacyUsers.length){for(const key of legacyKeys())localStorage.removeItem(key);}
    if(currentUser&&imported>0)currentUser=applyAccount((await api('me')).user);
  }catch{/* Keep old local data if any account could not be migrated; it will be retried on next visit. */}
  if(currentUser){
    bindAccountCounters();
    primaryChip=currentUser.chips[0]||'magnet';secondaryChip=currentUser.chips[1]||'blast';
    muted=currentUser.settings.muted;reduced=currentUser.settings.reduced;
    if(Array.isArray(currentUser.keybindings))bindings=currentUser.keybindings as typeof bindings;
    applySettings();enterMenu(false);
  }else if(sessionStorage.getItem('mining:guest')){bindAccountCounters();primaryChip=guestProfile.chips[0]||'magnet';secondaryChip=guestProfile.chips[1]||'blast';muted=guestProfile.settings.muted;reduced=guestProfile.settings.reduced;applySettings();enterMenu(true);}else show(authPanel,true);
})();

if (import.meta.hot) {
  import.meta.hot.dispose(() => game.dispose());
}

// expose for tests
(window as unknown as { __GAME_UI__?: unknown }).__GAME_UI__ = {
  openPlay,
  enterMenu,
  markAch,
  game,
};
